import { useSyncExternalStore } from 'react'
import type { AccessDetails, ClusterAccess } from '@bloo-team/api-client'
import type { ProductKey } from './nav'
import type { Identity, ProductTier, SecurityRole, UserType } from './permissions'

/**
 * THE session — one object the whole console reads from (single source of
 * truth). Carries the SSID, the real identity, and the full org/scope/
 * entitlement tree from access-details. Demo mode fabricates one of the same
 * shape; live mode fills it from GET /lc/users/access-details. Nothing
 * downstream cares which.
 *
 * Read it with useSession(); derive the per-cluster permission view with
 * identityFor(). The api-client's getSsid() and the route guards read the raw
 * store. Persisted in sessionStorage — a console session should not outlive
 * the browser (PLAN.md §7).
 */

export interface Cluster {
  clusterId: string
  clusterName: string
  /** Role at the cluster level (access_type). */
  role: string
  licenseType: ProductTier
  productType: string
  /** Per-product entitlement for this cluster — see {@link productEntitlements}. */
  products: ProductEntitlements
  scopes: Array<{ scopeId: string; scopeName: string; role: string }>
}

export interface SessionUser {
  userId: string
  orgId: string
  name: string
  email: string
  /** Raw backend userType, e.g. "Organization Admin". */
  userType: string
  isRoot: boolean
  isOrgAdmin: boolean
  isAdmin: boolean
}

export interface Session {
  ssid: string
  mode: 'demo' | 'live'
  user: SessionUser
  clusters: Cluster[]
  establishedAt: number
}

const KEY = 'bloo.session'
const listeners = new Set<() => void>()

function load(): Session | null {
  try {
    const raw = window.sessionStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as Session) : null
  } catch {
    return null
  }
}

let current: Session | null = typeof window !== 'undefined' ? load() : null

function write(next: Session | null, persist: boolean) {
  current = next
  if (persist) {
    if (next) window.sessionStorage.setItem(KEY, JSON.stringify(next))
    else window.sessionStorage.removeItem(KEY)
  }
  listeners.forEach((cb) => cb())
}

export function setSession(next: Session | null) {
  write(next, true)
}

/**
 * Set the session in memory only, leaving sessionStorage untouched.
 *
 * For the window where we hold an SSID but do not yet know what it can reach.
 * establishLive() has to put the SSID in the store before calling
 * access-details, because the api-client reads it via getSsid() — but that
 * interim value describes no clusters and no identity, and persisting it means
 * a reload mid-flight restores a session that every route guard accepts
 * (they test presence only) while `clusters` is empty. defaultLocation() then
 * resolves to empty ids and the console lands nowhere.
 *
 * Keeping it out of storage makes that window survivable: a reload falls back
 * to whatever was legitimately persisted before, which is either a real
 * session or none at all.
 */
export function setTransientSession(next: Session) {
  write(next, false)
}

export function getSession(): Session | null {
  return current
}

export function useSession(): Session | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => current,
  )
}

/* ----------------------------------------------------------------- mapping */

const TIERS: ProductTier[] = ['essential', 'professional', 'enterprise']
function toTier(licenseType: string): ProductTier {
  const t = licenseType.toLowerCase()
  return (TIERS.find((x) => x === t) ?? 'essential')
}

/** What each product is licensed at for a cluster; null = not entitled. */
export type ProductEntitlements = Record<ProductKey, ProductTier | null>

/**
 * Derive a cluster's per-product entitlements from its access-details row.
 *
 * BA-P1 FALLBACK (docs/BACKEND-ASKS.md): access-details does not yet carry a
 * per-product split, so until BA-P1 ships EVERY product follows the cluster's
 * `licenseType` — exactly the pre-modularization behavior. The moment the
 * payload carries `entitlements: { products: string[] }` per cluster (BA-P1;
 * note the api-client `clusterAccess` schema must admit the field in the same
 * change, or zod strips it), listed products take the cluster tier and
 * everything else goes null. This function is the ONE flip point — nothing
 * else in the console knows which side of BA-P1 it is running on.
 */
export function productEntitlements(c: {
  licenseType: string
  entitlements?: { products?: string[] }
}): ProductEntitlements {
  const tier = toTier(c.licenseType)
  const split = c.entitlements?.products
  const grant = (id: ProductKey): ProductTier | null => (split ? (split.includes(id) ? tier : null) : tier)
  return { fabric: grant('fabric'), vantage: grant('vantage'), synthai: grant('synthai') }
}

/** Raw backend userType → the model's. The live access-details sends "Root User" (captured 2026-10-16). */
const USER_TYPES: Record<string, UserType> = {
  root: 'Root',
  'root user': 'Root',
  'organization admin': 'OrgAdmin',
  orgadmin: 'OrgAdmin',
  administrator: 'OrgAdmin',
  'content manager': 'ContentManager',
  contentmanager: 'ContentManager',
  user: 'User',
}
function toUserType(raw: string): UserType {
  return USER_TYPES[raw.toLowerCase()] ?? 'User'
}

const ROLES: SecurityRole[] = ['Security Engineer', 'Security Analyst', 'Viewer', 'No Access']
function toRole(raw: string): SecurityRole {
  return ROLES.find((r) => r === raw) ?? 'Viewer'
}

/** Build a Session from the live access-details payload. */
export function sessionFromAccess(ssid: string, a: AccessDetails, mode: 'live' | 'demo'): Session {
  // The intersection type admits the BA-P1 `entitlements` field the zod
  // schema will someday carry — productEntitlements reads it when present.
  const cluster = (c: ClusterAccess & { entitlements?: { products?: string[] } }): Cluster => ({
    clusterId: c.clusterId,
    clusterName: c.clusterName,
    role: c.access_type,
    licenseType: toTier(c.licenseType),
    productType: c.productType,
    products: productEntitlements(c),
    scopes: c.scope_access.map((s) => ({
      scopeId: s.scope_id,
      scopeName: s.scope_name,
      role: s.role,
    })),
  })
  return {
    ssid,
    mode,
    user: {
      userId: a.userId ?? '',
      orgId: a.orgId ?? '',
      name: a.name ?? a.emailId ?? 'User',
      email: a.emailId ?? '',
      userType: a.userType ?? 'User',
      isRoot: a.isRoot ?? false,
      isOrgAdmin: a.isOrgAdmin ?? false,
      isAdmin: a.isAdmin ?? false,
    },
    clusters: a.access.map(cluster),
    establishedAt: Date.now(),
  }
}

/* ----------------------------------------------------------------- selectors */

export function findCluster(session: Session, clusterId: string): Cluster | undefined {
  return session.clusters.find((c) => c.clusterId === clusterId)
}

export function defaultLocation(session: Session): { clusterId: string; scopeId: string } {
  const c = session.clusters[0]
  return { clusterId: c?.clusterId ?? '', scopeId: c?.scopes[0]?.scopeId ?? '' }
}

/**
 * Derive the per-cluster Identity the permission model / nav / entitlements
 * consume. Role resolves against the active scope when given, else the cluster.
 * Products both take the cluster's tier — the Bloo product split isn't in the
 * backend entitlement yet, so licenseType gates both until it is.
 */
export function identityFor(session: Session, clusterId: string, scopeId?: string): Identity {
  const cluster = findCluster(session, clusterId) ?? session.clusters[0]
  const scope = cluster?.scopes.find((s) => s.scopeId === scopeId)
  const roleRaw = scope?.role ?? cluster?.role ?? 'Viewer'
  const tier: ProductTier = cluster ? cluster.licenseType : 'essential'
  const isTenantAdmin =
    session.user.isOrgAdmin || /admin/i.test(cluster?.role ?? '') || session.user.userType.includes('Admin')

  return {
    // access-details' isRoot is the authority; the userType string is a label that has varied ("Root", "Root User").
    userType: session.user.isRoot ? 'Root' : toUserType(session.user.userType),
    role: toRole(roleRaw),
    isTenantAdmin,
    tier,
    displayName: session.user.name,
    email: session.user.email,
    // Per-cluster split, derived once in sessionFromAccess/demoSession
    // (productEntitlements — BA-P1 fallback documented there). The `??` arm
    // covers a session persisted before the `products` field existed.
    products: cluster?.products ?? { fabric: tier, vantage: tier, synthai: tier },
  }
}

/* ----------------------------------------------------------------- demo seed */

/**
 * Demo identity from the sign-in email (docs/testing.md §Demo identity):
 * the local part selects the raw backend `userType` the session carries, so
 * a ContentManager-only gate (Data Model writes) is reachable in demo.
 *   content…@ → 'Content Manager' · root…@ → 'Root' · anything else → 'Organization Admin'
 * The security role is separate: viewer…@ signs in as a Viewer on every
 * cluster and scope (what Datafabric · Overview's plain-text links are
 * tested with); anyone else keeps the Security Engineer roles below.
 */
function demoUserType(local: string): string {
  const l = local.toLowerCase()
  if (l.startsWith('content')) return 'Content Manager'
  if (l.startsWith('root')) return 'Root'
  return 'Organization Admin'
}

/**
 * A fabricated session matching the real access-details of the demo tenant.
 *
 * Demo auth grants every registered product by default (requirements §5
 * step 6). `products` narrows the grant — the `?products=fabric,vantage`
 * fake, parsed and validated by the caller (lib/auth.ts /
 * lib/products.ts#parseProductsParam) — and is baked into the persisted
 * session, so a narrowed demo session survives reload.
 */
export function demoSession(email = 'demo@bloo.io', products?: readonly ProductKey[]): Session {
  const grant = (id: ProductKey): ProductTier | null =>
    !products || products.includes(id) ? 'enterprise' : null
  const demoProducts: ProductEntitlements = {
    fabric: grant('fabric'),
    vantage: grant('vantage'),
    synthai: grant('synthai'),
  }
  const local = email.split('@')[0] ?? 'Demo'
  const userType = demoUserType(local)
  const viewer = local.toLowerCase().startsWith('viewer')
  const role = (r: string) => (viewer ? 'Viewer' : r)
  return {
    ssid: crypto.randomUUID(),
    mode: 'demo',
    user: {
      userId: 'demo-user',
      orgId: 'demo-org',
      name: local,
      email,
      userType,
      isRoot: userType === 'Root',
      isOrgAdmin: userType === 'Organization Admin',
      isAdmin: false,
    },
    clusters: [
      {
        clusterId: '41655903-e3af-433d-972c-e3072c5a6f99',
        clusterName: 'WAYNE-CORP',
        role: role('Security Engineer'),
        licenseType: 'enterprise',
        productType: 'Enterprise',
        products: demoProducts,
        scopes: [{ scopeId: 'gotham', scopeName: 'GOTHAM', role: role('Security Engineer') }],
      },
      {
        clusterId: '0ae8c3e9-7e6b-4dcf-9528-a029d87ccb6a',
        clusterName: 'DNIFHQ',
        role: role('Administrator'),
        licenseType: 'enterprise',
        productType: 'Enterprise',
        products: demoProducts,
        scopes: [
          { scopeId: 'ps', scopeName: 'PS', role: role('Security Engineer') },
          { scopeId: 'monitoring', scopeName: 'Monitoring', role: role('Security Engineer') },
          { scopeId: 'sales', scopeName: 'SALES', role: role('Security Engineer') },
          { scopeId: 'training', scopeName: 'Training', role: role('Security Engineer') },
        ],
      },
    ],
    establishedAt: Date.now(),
  }
}
